// Disable unbound-method check for all backbone views/models because method bindings run after
// initialize and are therefore unsafe.
/* eslint-disable @typescript-eslint/unbound-method */
import Backbone, {AddOptions, EventsHash, View, ViewOptions} from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import Primer3TabState from 'analyses/Primer3TabState';
import * as analytics from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {Collection as SequencePrimerCollection, Model as SequencePrimer} from 'biojs/SequencePrimer';
import {ComponentsSequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import {PRIMER3_DIRECTIONS, Primer3Direction, Primer3MatchModel, Primer3Match} from 'molbio/MolBioConstants';
import {OligoToFormat, downloadOligos, getOligoFilename} from 'molbio/oligos/ExportOligoUtil';
import SequencePrimers from 'molbio/primers/backbone/SequencePrimers';
import notifications from 'notifications';
import preferences from 'Preferences';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import legacyRunModal from 'ui-library/legacyRunModal';
import {assertNever} from 'util/Types';

import PrimerPropertiesModal from '../molbio/PrimerPropertiesModal';
import EditPrimerTabState from './EditPrimerTabState';
import Primer3Menu from './Primer3Menu';
import Primer3Row from './Primer3Row';
import Primer3Tab from './Primer3Tab';

/**
 * Selection state for a primer row. If probe is disabled, then only forward and reverse need to be selected
 * for it to be considered ALL.
 */
enum Primer3RowSelectionState {
  NONE = 'NONE',
  SOME = 'SOME',
  ALL = 'ALL',
}

function hasSelectedPrimerToSave(row: Primer3MatchModel): boolean {
  if (row.get('saved')) {
    return false;
  }
  return (
    Boolean(row.get('forwardSelected')) ||
    Boolean(row.get('probeSelected')) ||
    Boolean(row.get('reverseSelected'))
  );
}

function isDirectionSelected(row: Primer3MatchModel, direction: Primer3Direction): boolean {
  switch (direction) {
    case 'forward':
      return Boolean(row.get('forwardSelected'));
    case 'probe':
      return Boolean(row.get('probeSelected'));
    case 'reverse':
      return Boolean(row.get('reverseSelected'));
    default:
      assertAndContinue(false, `Invalid direction: ${assertNever(direction)}`);
      return false;
  }
}

export class PrimerRowView extends Backbone.View<Primer3MatchModel> {
  primers!: {[direction in Primer3Direction]?: SequencePrimer};
  sequence!: ComponentsSequence;
  tabstate!: Primer3TabState;
  index!: number;

  static initClass(): void {
    this.prototype.tagName = 'tbody';
  }

  override events(): EventsHash {
    return {
      'change .primer3Row-allSelected': 'handleToggleAll',
      'change .primer3Row-forwardSelected': 'handleToggleForward',
      'change .primer3Row-probeSelected': 'handleToggleProbe',
      'change .primer3Row-reverseSelected': 'handleToggleReverse',
      'click tr': 'handleClick',
      'click .edit': 'edit',
    };
  }

  override initialize(
    options: ViewOptions<Primer3MatchModel> & {tabstate: Primer3TabState; index: number}
  ): void {
    this.tabstate = options.tabstate;
    this.index = options.index;
    this.sequence = this.tabstate.get('sequence');
    this.primers = {};
    for (const direction of PRIMER3_DIRECTIONS) {
      if (this.model.get(direction) != null) {
        this.primers[direction] = this.model.get(direction);
      }
    }
    this.tabstate = options.tabstate;
    this.listenTo(this.model, 'change:forwardSelected', this.updateUI);
    this.listenTo(this.model, 'change:probeSelected', this.updateUI);
    this.listenTo(this.model, 'change:reverseSelected', this.updateUI);
    this.listenTo(this.model, 'change:saved', this.updateUI);
  }

  handleClick(e: Event): void {
    if (!this.primers.forward!.isNew()) {
      e.preventDefault();
      return;
    }
    if (Array.from<EventTarget>(this.$('input[type="checkbox"]')).includes(e.target!)) {
      return;
    }
    this.handleToggleAll();
    if (this.getSelectionState() !== Primer3RowSelectionState.NONE) {
      this.sequence.trigger('jump', this.primers.forward!.start());
    }
  }

  handleToggleAll(): void {
    if (this.getSelectionState() === Primer3RowSelectionState.NONE) {
      analytics.track('primer', 'select_primers', {
        product: ANALYTICS_MODULE.MOLBIO,
        selection: 'row',
      });
      this.model.set({
        forwardSelected: true,
        probeSelected: Boolean(this.model.get('probe')),
        reverseSelected: true,
      });
    } else {
      this.model.set({forwardSelected: false, probeSelected: false, reverseSelected: false});
    }
  }

  trackTogglePrimer(selectedAttr: keyof Primer3Match, selection: string): void {
    if (!this.model.get(selectedAttr)) {
      analytics.track('primer', 'select_primers', {
        product: ANALYTICS_MODULE.MOLBIO,
        selection,
      });
    }
  }

  handleToggleForward(): void {
    this.trackTogglePrimer('forwardSelected', 'forward');
    this.model.set({forwardSelected: !this.model.get('forwardSelected')});
  }

  handleToggleProbe(): void {
    this.trackTogglePrimer('probeSelected', 'probe');
    this.model.set({probeSelected: !this.model.get('probeSelected')});
  }

  handleToggleReverse(): void {
    this.trackTogglePrimer('reverseSelected', 'reverse');
    this.model.set({reverseSelected: !this.model.get('reverseSelected')});
  }

  updateUI(): void {
    const forwardSelected = this.model.get('forwardSelected');
    const probeSelected = this.model.get('probeSelected');
    const reverseSelected = this.model.get('reverseSelected');
    this.$('.primer3Row-forwardSelected').prop('checked', Boolean(forwardSelected));
    this.$('.primer3Row-probeSelected').prop('checked', Boolean(probeSelected));
    this.$('.primer3Row-reverseSelected').prop('checked', Boolean(reverseSelected));

    const selectionState = this.getSelectionState();
    const $allSelected = this.$('.primer3Row-allSelected');
    if (selectionState === Primer3RowSelectionState.NONE) {
      $allSelected.prop('checked', false);
      $allSelected.prop('indeterminate', false);
    } else if (selectionState === Primer3RowSelectionState.SOME) {
      $allSelected.prop('checked', true);
      $allSelected.prop('indeterminate', true);
    } else {
      assertAndContinue(
        selectionState === Primer3RowSelectionState.ALL,
        `Unexpected selectionState: ${selectionState}`
      );
      $allSelected.prop('checked', true);
      $allSelected.prop('indeterminate', false);
    }

    this.$el.toggleClass('active', selectionState !== Primer3RowSelectionState.NONE);

    if (this.model.get('saved')) {
      this.$el.removeClass('active');
      this.$('tr').addClass('disabled').addClass('success');
      this.$('input[type=checkbox]').prop('disabled', true);
      this.$('.edit').addClass('hide');
    } else {
      const sequencePrimers = this.sequence._sequence().sequencePrimers();
      for (const direction of PRIMER3_DIRECTIONS) {
        if (isDirectionSelected(this.model, direction)) {
          sequencePrimers.add(this.primers[direction]!);
        } else {
          sequencePrimers.remove(this.primers[direction]!);
        }
      }
    }

    this.tabstate.trigger('selectChange');
  }

  getSelectionState(): Primer3RowSelectionState {
    const forwardSelected = this.model.get('forwardSelected');
    const probeSelected = this.model.get('probeSelected');
    const reverseSelected = this.model.get('reverseSelected');

    const isProbeEnabled = Boolean(this.model.get('probe'));
    assertAndContinue(
      isProbeEnabled || !probeSelected,
      'A primer3 result without a probe cannot have the probe checkbox selected.'
    );

    const numChecked = (forwardSelected ? 1 : 0) + (probeSelected ? 1 : 0) + (reverseSelected ? 1 : 0);
    if (numChecked === 0) {
      return Primer3RowSelectionState.NONE;
    } else if (numChecked >= (isProbeEnabled ? 3 : 2)) {
      return Primer3RowSelectionState.ALL;
    } else {
      return Primer3RowSelectionState.SOME;
    }
  }

  edit(e: Event): void {
    e.stopPropagation();
    EditPrimerTabState.editPair(this.primers.forward!.clone(), this.primers.reverse!.clone(), this.sequence);
  }

  override render(): this {
    this.$el.html(
      ReactDOMServer.renderToStaticMarkup(
        <Primer3Row model={this.model} primers={this.primers} sequence={this.sequence} />
      )
    );
    this.updateUI();
    setTestAttrs(this.$el, {component: 'Primer3Row', index: `${this.index}`});
    return this;
  }
}
PrimerRowView.initClass();

const COMPARATORS = {
  penalty(obj: Primer3MatchModel): number {
    return obj.get('penalty');
  },
  location(obj: Primer3MatchModel): number {
    return obj.get('forward').get('start');
  },
  productBP(obj: Primer3MatchModel): number {
    return obj.get('product_size');
  },
};

class OptionsView extends Backbone.View<Primer3TabState> {
  sequence!: ComponentsSequence;
  sequencePrimers!: SequencePrimers;
  primers!: Backbone.Collection<Primer3MatchModel>;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'navbar';
  }

  override events(): EventsHash {
    return {
      'click .js-save': 'save',
      'click .js-export-as-csv': 'exportAsCSV',
      'click a.sort-by': 'changeSort',
      'click .tm-params': 'tmParameters',
    };
  }

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this.sequencePrimers = this.sequence.sequencePrimers();
    this.listenTo(this.model, 'selectChange', this.renderSave);
    this.listenTo(this.model, 'change:saving', this.renderSave);
    this.primers = this.model.get('primers');
    this.primers.comparator = COMPARATORS.penalty;
    // eslint-disable-next-line no-restricted-syntax
    this.primers.sort();
  }

  changeSort(e: Event): void {
    e.preventDefault();
    const $target = $(e.currentTarget!);
    this.$('.primer-sort-by').text($target.text());
    if ($target.hasClass('sort-by-penalty')) {
      this.primers.comparator = COMPARATORS.penalty;
    } else if ($target.hasClass('sort-by-productBP')) {
      this.primers.comparator = COMPARATORS.productBP;
    } else {
      this.primers.comparator = COMPARATORS.location;
    }
    // eslint-disable-next-line no-restricted-syntax
    this.primers.sort();
  }

  tmParameters(): void {
    legacyRunModal(MeltingTempModal, {});
  }

  async save(): Promise<void> {
    analytics.track('primer', 'save_primers_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: this.sequence.get('id'),
    });
    const primerParams = await legacyRunModal(PrimerPropertiesModal, {});
    if (primerParams) {
      this.model.trigger('savePrimers', primerParams);
    }
  }

  exportAsCSV(): void {
    this.model.trigger('exportPrimersAsCSV');
  }

  renderSave(): void {
    const hasSelectedPrimer = this.model
      .get('primers')
      .find((primerRow: Primer3MatchModel) => hasSelectedPrimerToSave(primerRow));
    // Button is initialized with disabled:true, so we need this as a boolean
    // so that false will correctly override true, since x.prop('foo', undefined) does not override true
    const saving = Boolean(this.model.get('saving'));
    this.$('.js-save').prop('disabled', !hasSelectedPrimer || saving);
    this.$('.js-export-as-csv').prop('disabled', !hasSelectedPrimer);
  }

  override render(): this {
    this.$el.html(ReactDOMServer.renderToStaticMarkup(<Primer3Menu />));
    this.renderSave();
    return this;
  }
}
OptionsView.initClass();

class TabView extends Backbone.View<Primer3TabState> {
  sequence!: ComponentsSequence;
  rows!: Array<PrimerRowView>;

  static initClass(): void {
    this.prototype.className = 'primer3-view buffer-tab';
  }

  override events(): Backbone.EventsHash {
    return {
      'change .select-all': 'selectAll',
    };
  }

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this.listenTo(this.model.get('primers'), 'reset sort', this.render);
    this.rows = [];
    this.listenTo(this.model, 'removePrimers', this.removePrimers);
    this.listenTo(this.model, 'savePrimers', this.savePrimers);
    this.listenTo(this.model, 'exportPrimersAsCSV', this.exportPrimersAsCSV);
    this.listenTo(preferences, `change:${PrimerPreferences.MELTING_TEMP_DNA}`, this.render);
    this.listenTo(this.sequence._sequence(), 'change:startIndex', this.render);
    this.listenTo(this.sequence._sequence(), 'change:bases', this.checkIfResultsValidAndReRender);
  }

  selectAll(e: Event): void {
    const newCheckedState = $(e.currentTarget!).prop('checked');
    if (newCheckedState) {
      analytics.track('primer', 'select_primers', {
        product: ANALYTICS_MODULE.MOLBIO,
        selection: 'all',
      });
    }
    for (const primerRow of this.model.get('primers').models) {
      if (primerRow.get('saved')) {
        continue;
      }
      primerRow.set({
        forwardSelected: newCheckedState,
        probeSelected: primerRow.get('probe') != null && newCheckedState,
        reverseSelected: newCheckedState,
      });
    }
  }

  override render(): this {
    const basesChanged = this.model.haveBasesChanged();
    this.$el.html(ReactDOMServer.renderToStaticMarkup(<Primer3Tab basesChanged={basesChanged} />));
    this.removeRows();
    if (!basesChanged) {
      this.rows = this.model.get('primers').map((match: Primer3MatchModel, index: number) => {
        const row = new PrimerRowView({
          tabstate: this.model,
          model: match,
          index,
        } as ViewOptions<Primer3MatchModel>);
        this.$('table').append(row.render().el);
        return row;
      });
    }
    this.$('.glyphicon-question-sign').popover({
      placement: 'right',
      title: 'Penalty',
      content: `This is the \
<a href="http://primer3.ut.ee/primer3web_help.htm#calculatePenalties" target="_blank">Primer3 penalty \
score</a>. Lower penalties are better.`,
      html: true,
    });
    return this;
  }

  getNewPrimers(): Array<SequencePrimer> {
    const newPrimers: Array<SequencePrimer> = [];
    for (const row of this.rows) {
      for (const direction of PRIMER3_DIRECTIONS) {
        if (isDirectionSelected(row.model, direction)) {
          newPrimers.push(row.primers[direction]!);
        }
      }
    }
    return newPrimers;
  }

  removePrimers(): void {
    for (const primer of this.getNewPrimers()) {
      primer.destroy();
    }
  }

  /**
   * We get the next index by searching through all the names of
   * the previous primers created by the wizard. The index is always located
   * at the end of the generated name.
   */
  getNextAvailablePrimerPairIndex(sequencePrimers: Backbone.Collection<SequencePrimer>): number {
    let nextPrimerPairIndex = 1;
    for (let i = 0; i < sequencePrimers.length; i++) {
      const regex = /^.+ \d+-\d+ (?:(?:FWD)|(?:REV)) (\d+)$/;
      const matches = regex.exec(sequencePrimers.models[i].attributes.name);
      if (matches) {
        const primerPairIndex = Number(matches[1]);
        if (primerPairIndex >= nextPrimerPairIndex) {
          nextPrimerPairIndex = primerPairIndex + 1;
        }
      }
    }
    return nextPrimerPairIndex;
  }

  savePrimers({folderId, tagSchemaId}: {folderId: string; tagSchemaId: string}): void {
    if (this.getNewPrimers().length === 0) {
      notifications.error('Select new primers to add first.');
      return;
    }
    this.model.set({saving: true});
    const _sequencePrimers = this.sequence.get('_sequence').sequencePrimers();

    const nextIndex = this.getNextAvailablePrimerPairIndex(_sequencePrimers);

    const _primerPairs = this.sequence.get('_sequence').primerPairs();

    const toAdd = new SequencePrimerCollection([], {sequenceID: this.sequence.id});
    const selectedRows = this.rows.filter((row) => hasSelectedPrimerToSave(row.model));
    for (let pairId = 0; pairId < selectedRows.length; pairId++) {
      const row = selectedRows[pairId];
      for (const [direction, primer] of Object.entries(row.primers) as Array<
        [Primer3Direction, SequencePrimer]
      >) {
        if (!isDirectionSelected(row.model, direction)) {
          continue;
        }
        // The name of the primer has to be changed after the primers
        // are generated since the name of the primers are generated before
        // the primer gets saved. Thus if the index is created beforehand
        // the saved primers will have non consecutive indices because some of
        // the generated primers will not be saved.
        primer.set('name', `${primer.get('name')} ${(nextIndex + pairId).toString()}`);

        if (
          direction !== 'probe' &&
          isDirectionSelected(row.model, 'forward') &&
          isDirectionSelected(row.model, 'reverse')
        ) {
          // Only include the pairId if this is part of the pair (forward or reverse) and if we know that both
          // parts of the pair are selected.
          toAdd.add({...primer.toJSON(), tagSchemaId, folder: folderId, pairId});
        } else {
          toAdd.add({...primer.toJSON(), tagSchemaId, folder: folderId});
        }
      }
    }
    Backbone.sync('create', toAdd, {fileUpdate: true} as JQueryAjaxSettings)
      .always(() => this.model.set({saving: false}))
      .fail(notifications.notifyError)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .done(({primers, primerPairs, message}: any) => {
        this.removePrimers();
        this.sequence.updateVersions();
        for (const row of this.rows) {
          if (hasSelectedPrimerToSave(row.model)) {
            row.model.set({saved: true});
          }
        }
        _sequencePrimers.batchModify(function modify(this: Backbone.Collection<SequencePrimer>): void {
          this.add(primers, {parse: true} as AddOptions);
        });
        _primerPairs.batchModify(function modify(this: Backbone.Collection<SequencePrimer>): void {
          this.add(primerPairs, {parse: true} as AddOptions);
        });
        notifications.success(message);
        analytics.track('primer', 'added_primers', {
          product: ANALYTICS_MODULE.MOLBIO,
          source: 'primer3',
          sequence_api_id: this.sequence.id,
          primer_count: primers.length,
        });
      });
  }

  exportPrimersAsCSV(): void {
    const selectedRows = this.rows.filter((row) => hasSelectedPrimerToSave(row.model));
    const oligos: Array<OligoToFormat> = [];

    for (let pairId = 0; pairId < selectedRows.length; pairId++) {
      const row = selectedRows[pairId];
      for (const [direction, primer] of Object.entries(row.primers) as Array<
        [Primer3Direction, SequencePrimer]
      >) {
        if (!isDirectionSelected(row.model, direction)) {
          continue;
        }
        oligos.push({
          name: `${primer.get('name')} primer3 pair ${pairId.toString()}`,
          bases: primer.get('bases'),
          nucleotideType: primer.nucleotideType(),
        });
      }
    }
    downloadOligos({oligos, filename: getOligoFilename(this.sequence.name())});
  }

  removeRows(): void {
    for (const row of this.rows) {
      row.stopListening();
    }
    this.rows = [];
  }

  override remove(): View<Primer3TabState> {
    this.removeRows();
    return super.remove();
  }

  // If the current bases are different from the original bases, then the primer3 results
  // are invalid and we must unselect the primers.
  checkIfResultsValidAndReRender(): void {
    if (this.model.haveBasesChanged()) {
      this.model.get('primers').forEach((model: Primer3MatchModel) => {
        model.set({forwardSelected: false, probeSelected: false, reverseSelected: false});
      });
    }
    this.render();
  }
}
TabView.initClass();

export {OptionsView, TabView};
